import * as React from 'react';
import master from '../../Master';
import { Input } from '../Input';
import { Button } from '../Button';
import { IInputField } from './InputFieldsInput';
import { IAdditionalField } from './AdditoinalFieldsInput';
import { IOutputField } from './OutputFieldsInput';
import { Validated } from '../Validated';
import { LineSelect, ArrowEndSelect, EnclosingSvgSelect } from '../ImageSelect';

import './DrawingObjectInput.css';
import { TableScroll } from '../TableScroll';

export interface IDrawingObject {
  id?: number;
  fieldType: FieldType;
  fieldIndex: number;
  prefix: string;
  suffix: string;
  digit: number;
  lineType: number;
  arrowEndType: number;
  placeEnclosingSvgId?: number | null;
}

export enum FieldType {
  InputField = 1,
  AdditionalField,
  OutputField,
}

interface IEnclosingSvg {
  id: number;
  svg: string;
}

interface DrawingObjectsInputProps {
  readonly value: IDrawingObject[];
  readonly inputFields: IInputField[];
  readonly additionalFields: IAdditionalField[];
  readonly outputFields: IOutputField[];
  readonly enclosingSvgs: IEnclosingSvg[];
  readonly validation: string | undefined;
  /** 誤りがあるため赤くする行の位置 */
  readonly invalidRowIndexes: number[];
  onChange: (objects: IDrawingObject[]) => void;
}

/**
 * 作画文字列のうち、図形の種類が持たない自動計測項目を使っている行の位置を返す。
 * shapeInputFieldIdsは、図形の種類が持つ自動計測項目のid
 */
export function findMissingInputFieldRowIndexes(
  shapeInputFieldIds: number[],
  drawingObjects: IDrawingObject[]
): number[] {
  const indexes: number[] = [];
  drawingObjects.forEach((obj, index) => {
    if (obj.fieldType === FieldType.InputField && shapeInputFieldIds.indexOf(obj.fieldIndex) === -1) {
      indexes.push(index);
    }
  });
  return indexes;
}

/** 作画文字列が使っている自動計測項目のうち、図形の種類が持たない項目の名前を、重複を除いて返す */
export function findMissingInputFieldNames(
  shapeInputFieldIds: number[],
  drawingObjects: IDrawingObject[]
): string[] {
  const names: string[] = [];
  for (const index of findMissingInputFieldRowIndexes(shapeInputFieldIds, drawingObjects)) {
    const field = master.InputFields.find((f) => f.id === drawingObjects[index].fieldIndex);
    if (field && names.indexOf(field.name) === -1) {
      names.push(field.name);
    }
  }
  return names;
}

/**
 * 作画文字列の項目名の選択欄で、図形の種類にない自動計測項目を選んだままの行に出す選択肢。
 * 選択欄が別の項目を選んでいるように見えないよう、今の値を「図形の種類にない項目」として並べる
 */
function renderMissingInputFieldOption(obj: IDrawingObject, inputFields: IInputField[]): React.ReactNode {
  if (obj.fieldType !== FieldType.InputField) {
    return null;
  }
  if (inputFields.find((f) => f.master.id === obj.fieldIndex)) {
    return null;
  }
  const field = master.InputFields.find((f) => f.id === obj.fieldIndex);
  if (!field) {
    return null;
  }
  return <option value={field.id}>{field.name}（図形の種類にない項目）</option>;
}

export const DrawingObjectsInput: React.FC<DrawingObjectsInputProps> = function (
  props
) {
  function changeValue(index: number, obj: IDrawingObject) {
    const next = props.value.slice();
    next[index] = obj;
    props.onChange(next);
  }
  function addValue() {
    props.onChange([...props.value, defaultDrawingObject()]);
  }
  function removeValue(index: number) {
    props.onChange(props.value.filter((_, i) => i !== index));
  }

  function defaultDrawingObject(): IDrawingObject {
    return {
      fieldType: FieldType.InputField,
      fieldIndex:
        props.inputFields.length === 0 ? 0 : props.inputFields[0].master.id,
      prefix: '',
      suffix: '',
      lineType: 0,
      arrowEndType: 0,
      digit: 2,
      placeEnclosingSvgId: null,
    };
  }

  function row(obj: IDrawingObject, index: number) {
    return (
      <tr key={index} className={getFieldRowClassName(props.invalidRowIndexes.indexOf(index) !== -1)}>
        <td>
          <select
            value={obj.fieldType}
            onChange={(event) => {
              let fieldType: FieldType;
              let initialFieldIndex: number;
              switch (event.target.value) {
                case '1':
                  // 行を足したときと同じく、図形の種類が持つ項目の先頭を選ぶ
                  fieldType = FieldType.InputField;
                  initialFieldIndex = props.inputFields.length === 0 ? 0 : props.inputFields[0].master.id;
                  break;
                case '2':
                  fieldType = FieldType.AdditionalField;
                  initialFieldIndex = 0;
                  break;
                case '3':
                  fieldType = FieldType.OutputField;
                  initialFieldIndex = 0;
                  break;
                default:
                  throw new Error('invalid type');
              }

              changeValue(index, {
                ...obj,
                fieldType,
                fieldIndex: initialFieldIndex,
              });
            }}
          >
            <option value={FieldType.InputField}>自動計測項目</option>
            <option value={FieldType.AdditionalField}>手動入力項目</option>
            <option value={FieldType.OutputField}>計算結果項目</option>
          </select>
        </td>
        <td>
          {(() => {
            switch (obj.fieldType) {
              case FieldType.InputField:
                if (props.inputFields.length === 0) {
                  return <p>項目がありません</p>;
                }
                break;
              case FieldType.AdditionalField:
                if (props.additionalFields.length === 0) {
                  return <p>項目がありません</p>;
                }
                break;
              case FieldType.OutputField:
                if (props.outputFields.length === 0) {
                  return <p>項目がありません</p>;
                }
                break;
            }

            return (
              <select
                value={obj.fieldIndex}
                onChange={(event) =>
                  changeValue(index, {
                    ...obj,
                    fieldIndex: parseInt(event.target.value),
                  })
                }
              >
                {(function () {
                  switch (obj.fieldType) {
                    case FieldType.InputField:
                      return (
                        <>
                          {props.inputFields.map((f) => (
                            <option key={`input_field-${f.master.id}`} value={f.master.id}>{f.master.name}</option>
                          ))}
                          {renderMissingInputFieldOption(obj, props.inputFields)}
                        </>
                      );
                    case FieldType.AdditionalField:
                      return props.additionalFields.map((f, i) => (
                        <option key={`additional_field-${i}`} value={i}>{f.name}</option>
                      ));
                    case FieldType.OutputField:
                      return props.outputFields.map((f, i) => (
                        <option key={`output_field-${i}`} value={i}>{f.name}</option>
                      ));
                  }
                })()}
              </select>
            );
          })()}
        </td>
        <td>
          <Input
            className="DrawingObjectInput-text_input"
            value={obj.prefix}
            onChange={(event) =>
              changeValue(index, { ...obj, prefix: event.target.value })
            }
          />
        </td>
        <td>
          <Input
            className="DrawingObjectInput-text_input"
            value={obj.suffix}
            onChange={(event) =>
              changeValue(index, { ...obj, suffix: event.target.value })
            }
          />
        </td>
        <td>
          <Input
            className="DrawingObjectInput-number_input"
            value={obj.digit.toString()}
            type="number"
            onChange={(event) =>
              changeValue(index, {
                ...obj,
                digit: parseInt(event.target.value),
              })
            }
          />
        </td>
        <td>
          <LineSelect
            value={obj.lineType}
            onChange={(value) =>
              changeValue(index, { ...obj, lineType: value })
            }
            optionClassName="DrawingObjectInput-image_option"
            containerClassName="ImageSelect-container"
          />
        </td>
        <td>
          <ArrowEndSelect
            value={obj.arrowEndType}
            onChange={(value) =>
              changeValue(index, { ...obj, arrowEndType: value })
            }
            optionClassName="DrawingObjectInput-image_option"
            containerClassName="ImageSelect-container"
          />
        </td>
        <td>
          <EnclosingSvgSelect
            enclosingSvgs={props.enclosingSvgs}
            value={obj.placeEnclosingSvgId}
            onChange={(value) =>
              changeValue(index, { ...obj, placeEnclosingSvgId: value })
            }
            optionClassName="DrawingObjectInput-image_option EnclosingSvgSelect-button"
            containerClassName="ImageSelect-container"
          />
        </td>
        <td className="App-table_button_cell">
          <Button type="button" mini={true} color="red" onClick={() => removeValue(index)}>
            削除
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <div>
      <Validated message={props.validation}>
        <TableScroll storageKey="codeDrawingObjects">
          <table className="App-table">
            <thead>
              <tr>
                <th>項目の種類</th>
                <th>項目名</th>
                <th>前置文字列</th>
                <th>後置文字列</th>
                <th>表示桁数</th>
                <th>線種</th>
                <th>矢印</th>
                <th>囲み図形</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>{props.value.map(row)}</tbody>
          </table>
        </TableScroll>
      </Validated>
      <div className="PlaceCreateCodePage-table_add_row">
        <Button type="button" mini={true} color="white" onClick={() => addValue()}>
          行を追加
        </Button>
      </div>
    </div>
  );
};

/** 補修コードの編集画面の表で、入力チェックで誤りが見つかった行に付けるクラス名を返す */
export function getFieldRowClassName(invalid: boolean): string | undefined {
  if (invalid) {
    return 'PlaceCreateCodePage-invalid_row';
  }
  return undefined;
}

/** 作画文字列に参照されている項目の削除ボタンに、削除できない理由として出す文を返す */
export function getReferencedFieldRemoveTitle(referenced: boolean): string | undefined {
  if (referenced) {
    return '図面上に作画する文字列で参照されているため削除できません';
  }
  return undefined;
}
